import { useState } from 'react';
import { ChevronDown, ChevronUp, Server, Monitor, Database } from 'lucide-react';
import { Badge } from '@makanly/ui-kit';
import type { ServiceStatus, ServiceCategory } from '../types';
import { categoryLabels, calculateHealthSummary } from '../types';
import { StatusCard } from './StatusCard';

interface ServiceGroupProps {
  category: ServiceCategory;
  services: ServiceStatus[];
}

const categoryIconMap = {
  backend: Server,
  frontend: Monitor,
  infrastructure: Database,
};

export function ServiceGroup({ category, services }: ServiceGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const Icon = categoryIconMap[category];
  const label = categoryLabels[category];
  const summary = calculateHealthSummary(services);

  const hasUnhealthy = summary.unhealthy > 0;

  return (
    <div className="mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          w-full flex items-center justify-between p-3 rounded-lg
          transition-colors duration-200
          ${hasUnhealthy ? 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50' : 'bg-secondary hover:bg-accent'}
        `}
      >
        <div className="flex items-center gap-3">
          <Icon
            className={`w-5 h-5 ${hasUnhealthy ? 'text-red-600 dark:text-red-400' : 'text-text-secondary'}`}
          />
          <span className="font-semibold text-text-primary">{label}</span>
          <Badge variant={hasUnhealthy ? 'destructive' : 'secondary'}>
            {summary.healthy}/{summary.total}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          {hasUnhealthy && (
            <span className="text-xs text-red-600 dark:text-red-400 font-medium">
              {summary.unhealthy} down
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-text-secondary" />
          ) : (
            <ChevronDown className="w-4 h-4 text-text-secondary" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {services.map((service) => (
            <StatusCard key={`${service.name}-${service.port}`} service={service} />
          ))}
        </div>
      )}
    </div>
  );
}
